import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Tabs,
	Tab,
	TextField,
	Button,
	Grid2,
	Skeleton,
	Checkbox,
	InputAdornment,
	IconButton,
	Link as MuiLink,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify/iconify';
import config from '../common/Config';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import '../common/commonStyles.css';

const PushSage = () => {
	const [activeTab, setActiveTab] = useState('notPushed');
	const [notPushedList, setNotPushedList] = useState([]);
	const [pushedList, setPushedList] = useState([]);
	const [totalPushed, setTotalPushed] = useState(0);
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [pushedDatesDraft, setPushedDatesDraft] = useState([null, null]);
	const [pushedDates, setPushedDates] = useState([null, null]);
	const [selectedIds, setSelectedIds] = useState([]);
	const [pushingRowId, setPushingRowId] = useState(null);
	const [pushingSelected, setPushingSelected] = useState(false);
	const [confirmDialog, setConfirmDialog] = useState({
		open: false,
		title: '',
		message: '',
		onConfirm: null,
	});
	const [reload, setReload] = useState(false);
	const token = localStorage.getItem('accessToken');

	// Not Pushed list is fetched in full and filtered/paginated client-side
	const fetchNotPushed = useCallback(async () => {
		setLoading(true);
		try {
			const res = await axios.post(
				`${config.API_URL}/fetch_not_pushed_list`,
				{},
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setNotPushedList(res.data?.data || []);
		} catch {
			toast.error('Could not load invoices to push');
			setNotPushedList([]);
		} finally {
			setLoading(false);
		}
	}, [token]);

	// Pushed list is paginated/search/date-filtered server-side
	const fetchPushed = useCallback(async () => {
		setLoading(true);
		try {
			const [startDate, endDate] = pushedDates;
			const formData = new FormData();
			formData.append('draw', 1);
			formData.append('start', page * rowsPerPage);
			formData.append('length', rowsPerPage);
			formData.append('search[value]', searchTerm || '');
			formData.append('searchByFromdate', startDate ? new Date(startDate).toLocaleDateString('en-CA') : '');
			formData.append('searchByTodate', endDate ? new Date(endDate).toLocaleDateString('en-CA') : '');

			const response = await fetch(`${config.API_URL}/pushall/invoices`, {
				method: 'POST',
				headers: { authorization: `Bearer ${token}` },
				body: formData,
			});
			const json = await response.json();
			const data = Array.isArray(json?.data) ? json.data : [];
			setPushedList(data);
			setTotalPushed(json.recordsFiltered || json.recordsTotal || data.length);
		} catch {
			toast.error('Could not load pushed invoices');
			setPushedList([]);
			setTotalPushed(0);
		} finally {
			setLoading(false);
		}
	}, [token, page, rowsPerPage, searchTerm, pushedDates]);

	useEffect(() => {
		if (!token) {
			window.location.href = `${config.BASE_URL}/login`;
			return;
		}
	}, [token]);

	useEffect(() => {
		if (activeTab === 'notPushed') fetchNotPushed();
	}, [activeTab, fetchNotPushed, reload]);

	useEffect(() => {
		if (activeTab === 'pushed') fetchPushed();
	}, [activeTab, fetchPushed]);

	// Selection is tied to the rows currently loaded -- drop it whenever the list is refetched
	useEffect(() => {
		setSelectedIds([]);
	}, [notPushedList, activeTab]);

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
		setPage(0);
		setSearchTerm('');
	};

	const filteredNotPushed = notPushedList.filter((row) => {
		if (!searchTerm) return true;
		const q = searchTerm.toLowerCase();
		return (
			String(row.invoice_number || '').toLowerCase().includes(q) ||
			String(row.customer || '').toLowerCase().includes(q) ||
			String(row.invoice_date || '').toLowerCase().includes(q)
		);
	});
	const pagedNotPushed = filteredNotPushed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const handleChangePage = (event, newPage) => setPage(newPage);
	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handlePushedDateSubmit = () => {
		setPushedDates(pushedDatesDraft);
		setPage(0);
	};

	const closeConfirmDialog = () =>
		setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });

	const pushInvoice = (id) => {
		if (!id) return toast.error('Invalid invoice ID');
		setConfirmDialog({
			open: true,
			title: 'Push to Sage?',
			message: 'Are you sure you want to push this invoice to Sage?',
			onConfirm: async () => {
				closeConfirmDialog();
				setPushingRowId(id);
				try {
					const fd = new FormData();
					fd.append('id', id);
					const r = await axios.post(`${config.API_URL}/push_invoice`, fd, {
						headers: { Authorization: `Bearer ${token}` },
					});
					if (r.data.success) {
						toast.success(r.data.message || 'Invoice pushed to Sage');
						setReload((prev) => !prev);
					} else {
						toast.error(r.data.message || 'Push failed');
					}
				} catch {
					toast.error('Something went wrong');
				} finally {
					setPushingRowId(null);
				}
			},
		});
	};

	const pushSelectedInvoices = () => {
		if (!selectedIds.length) return;
		setConfirmDialog({
			open: true,
			title: 'Push to Sage?',
			message: `Are you sure you want to push ${selectedIds.length} selected invoice(s) to Sage?`,
			onConfirm: async () => {
				closeConfirmDialog();
				setPushingSelected(true);
				try {
					const fd = new FormData();
					selectedIds.forEach((id) => fd.append('id[]', id));
					const r = await axios.post(`${config.API_URL}/bulk_push_invoice`, fd, {
						headers: { Authorization: `Bearer ${token}` },
					});
					if (r.data.success) {
						toast.success(r.data.message || `${selectedIds.length} invoice(s) pushed to Sage`);
						setSelectedIds([]);
						setReload((prev) => !prev);
					} else {
						toast.error(r.data.message || 'Push failed');
					}
				} catch {
					toast.error('Something went wrong');
				} finally {
					setPushingSelected(false);
				}
			},
		});
	};

	const toggleSelectAll = (checked) => {
		setSelectedIds(checked ? pagedNotPushed.map((row) => String(row.id)) : []);
	};

	const toggleSelectRow = (id, checked) => {
		const key = String(id);
		setSelectedIds((prev) => {
			const next = new Set(prev);
			checked ? next.add(key) : next.delete(key);
			return Array.from(next);
		});
	};

	const isNotPushed = activeTab === 'notPushed';
	const rows = isNotPushed ? pagedNotPushed : pushedList;
	const totalCount = isNotPushed ? filteredNotPushed.length : totalPushed;
	const allOnPageSelected = pagedNotPushed.length > 0 && pagedNotPushed.every((row) => selectedIds.includes(String(row.id)));

	return (
		<Box className="page-container">
			<Paper className="paper-style">
				<Box sx={{ px: 2, py: 1.2 }}>
					<Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 1, gap: 2 }}>
						<Typography className="page-title">Push to Sage</Typography>
					</Box>
					<Tabs className="common-tabs" sx={{ my: 1 }} value={activeTab} onChange={handleTabChange}>
						<Tab label="Not Pushed" value="notPushed" />
						<Tab label="Pushed Invoices" value="pushed" />
					</Tabs>
					<Grid2 container spacing={2} alignItems="flex-end">
						<Grid2 size={{ xs: 12, md: 3 }}>
							<TextField
								className="input-border"
								fullWidth
								placeholder="Search..."
								value={searchTerm}
								onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
								size="small"
								slotProps={{
									input: {
										startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-outline" width={20} sx={{ color: '#637381' }} /></InputAdornment>,
										endAdornment: searchTerm && <InputAdornment position="end"><IconButton size="small" onClick={() => { setSearchTerm(''); setPage(0); }} sx={{ color: '#637381' }}><Iconify icon="eva:close-fill" width={20} /></IconButton></InputAdornment>,
									},
								}}
							/>
						</Grid2>
						{!isNotPushed && (
							<>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<CommonDatePicker className="input-border" value={pushedDatesDraft} onChange={setPushedDatesDraft} format="dd/MM/yyyy" character=" - " />
								</Grid2>
								<Grid2 size={{ xs: 12, md: 2 }}>
									<Button variant="contained" onClick={handlePushedDateSubmit} fullWidth className="button-variant-contained">Submit</Button>
								</Grid2>
							</>
						)}
						{isNotPushed && (
							<Grid2 size={{ xs: 12, md: 3 }} sx={{ ml: 'auto' }} display="flex" justifyContent="flex-end">
								<Button
									variant="contained"
									disabled={!selectedIds.length || pushingSelected}
									onClick={pushSelectedInvoices}
									className="button-variant-contained"
									startIcon={<Iconify icon="mingcute:send-line" width={16} />}
								>
									{pushingSelected ? 'Pushing...' : `Push Selected (${selectedIds.length})`}
								</Button>
							</Grid2>
						)}
					</Grid2>
				</Box>
				<TableContainer>
					<Table size="medium" className="table-cell-border" sx={{ tableLayout: 'fixed' }}>
						<TableHead>
							<TableRow className="table-cell-header">
								{isNotPushed && (
									<TableCell width="50px" padding="checkbox">
										<Checkbox
											size="small"
											checked={allOnPageSelected}
											indeterminate={selectedIds.length > 0 && !allOnPageSelected}
											onChange={(e) => toggleSelectAll(e.target.checked)}
										/>
									</TableCell>
								)}
								<TableCell width="60px">Sr.no</TableCell>
								{isNotPushed ? (
									<>
										<TableCell width="140px">Invoice Number</TableCell>
										<TableCell>Customer Name (Ac/No)</TableCell>
										<TableCell width="110px" align="right">Amount</TableCell>
										<TableCell width="120px" align="center">Action</TableCell>
									</>
								) : (
									<>
										<TableCell width="120px">Invoiced Date</TableCell>
										<TableCell width="140px">Invoice Number</TableCell>
										<TableCell>Customer Name (Ac/No)</TableCell>
										<TableCell width="130px" align="right">Invoice Amount</TableCell>
									</>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								Array.from({ length: 5 }).map((_, index) => (
									<TableRow key={index}>
										<TableCell colSpan={isNotPushed ? 6 : 5} sx={{ py: 1.5 }}>
											<Skeleton height={40} />
										</TableCell>
									</TableRow>
								))
							) : rows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={isNotPushed ? 6 : 5} align="center" sx={{ py: 4 }}>
										<Typography variant="body2" color="text.secondary">
											{isNotPushed ? 'No invoices to push.' : 'No records found.'}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								rows.map((row, index) => (
									<TableRow key={row.id || index} hover className="table-cell-style">
										{isNotPushed && (
											<TableCell padding="checkbox">
												<Checkbox
													size="small"
													checked={selectedIds.includes(String(row.id))}
													onChange={(e) => toggleSelectRow(row.id, e.target.checked)}
												/>
											</TableCell>
										)}
										<TableCell>{index + 1 + page * rowsPerPage}</TableCell>
										{isNotPushed ? (
											<>
												<TableCell>
													<MuiLink
														href={`${config.BASE_URL}/trip_summary/print/${row.id}`}
														target="_blank"
														rel="noopener noreferrer"
														sx={{ color: '#212b36', textDecoration: 'underline !important', '&:hover': { textDecoration: 'underline !important' } }}
													>
														{row.invoice_number || '-'}
													</MuiLink>
												</TableCell>
												<TableCell>{row.customer || '-'}</TableCell>
												<TableCell align="right">{parseFloat(row.invoice_amount || 0).toFixed(2)}</TableCell>
												<TableCell align="center">
													<Button
														variant="contained"
														size="small"
														disabled={pushingRowId === row.id}
														onClick={() => pushInvoice(row.id)}
														className="button-variant-contained"
													>
														{pushingRowId === row.id ? 'Pushing...' : 'Push'}
													</Button>
												</TableCell>
											</>
										) : (
											<>
												<TableCell>{row.invoice_date || '-'}</TableCell>
												<TableCell>
													<MuiLink
														href={`${config.BASE_URL}/trip_summary/print/${row.id}`}
														target="_blank"
														rel="noopener noreferrer"
														sx={{ color: '#212b36', textDecoration: 'underline !important', '&:hover': { textDecoration: 'underline !important' } }}
													>
														{row.invoice_number || '-'}
													</MuiLink>
												</TableCell>
												<TableCell>{row.customer || '-'}</TableCell>
												<TableCell align="right">{parseFloat(row.invoice_amount || 0).toFixed(2)}</TableCell>
											</>
										)}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				{!loading && rows.length > 0 && (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', borderTop: '1px solid rgba(145, 158, 171, 0.16)' }}>
						<TablePagination
							component="div"
							count={totalCount}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[25, 50, 100]}
							className="pagination-styles"
						/>
					</Box>
				)}
			</Paper>

			<Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
				<DialogTitle>{confirmDialog.title}</DialogTitle>
				<DialogContent>
					<DialogContentText>{confirmDialog.message}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeConfirmDialog} color="inherit" variant="outlined" className="button-variant-outlined">
						Cancel
					</Button>
					<Button
						onClick={() => confirmDialog.onConfirm && confirmDialog.onConfirm()}
						variant="contained"
						className="button-variant-contained"
						autoFocus
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PushSage;
